/**
 * @fileoverview Integration Settings Page
 * Displays all available integrations with their status, configuration options,
 * and sync logs. Users can connect/disconnect and configure integrations here.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MessageSquare,
  Building2,
  Plug2,
  History,
  Loader2,
} from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { IntegrationCard, IntegrationStatus } from '@/components/integrations/IntegrationCard';
import { SlackConfig } from '@/components/integrations/SlackConfig';
import { HubSpotConfig } from '@/components/integrations/HubSpotConfig';
import { GoogleConfig } from '@/components/integrations/GoogleConfig';

interface Integration {
  id: string;
  provider: string;
  status: 'active' | 'disconnected' | 'error';
  config: Record<string, unknown>;
  last_error?: string;
  last_sync_at?: string;
  created_at: string;
}

interface SyncLog {
  id: string;
  integration_id: string;
  provider: string;
  action: string;
  status: 'success' | 'error';
  details?: Record<string, unknown>;
  created_at: string;
}

// Google icon component
const GoogleIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Available integrations configuration
const AVAILABLE_INTEGRATIONS = [
  {
    id: 'slack',
    name: 'Slack',
    description: 'Receive call notifications in your Slack workspace',
    icon: <MessageSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />,
    iconBgClass: 'bg-purple-100 dark:bg-purple-900',
    category: 'notifications',
    comingSoon: false,
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    description: 'Sync contacts and log calls to HubSpot',
    icon: <Building2 className="h-6 w-6 text-orange-600 dark:text-orange-400" />,
    iconBgClass: 'bg-orange-100 dark:bg-orange-900',
    category: 'crm',
    comingSoon: false,
  },
  {
    id: 'google',
    name: 'Google',
    description: 'Connect Calendar and Sheets for scheduling and exports',
    icon: <GoogleIcon />,
    iconBgClass: 'bg-gray-100 dark:bg-gray-800',
    category: 'productivity',
    comingSoon: false,
  },
];

export function IntegrationsPage() {
  const { activeTenantId } = useAuth();
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);

  // Fetch all integrations for the tenant
  const { data: integrations, isLoading: isLoadingIntegrations } = useQuery({
    queryKey: ['integrations', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return [];

      const { data, error } = await supabase
        .from('calie_integrations')
        .select('*')
        .eq('tenant_id', activeTenantId);

      if (error) throw error;
      return (data || []) as Integration[];
    },
    enabled: !!activeTenantId,
  });

  // Fetch sync logs (recent activity)
  const { data: syncLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['integration-logs', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return [];

      const { data, error } = await supabase
        .from('calie_integration_logs')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        // Table might not exist yet, return empty
        console.warn('Could not fetch integration logs:', error.message);
        return [];
      }
      return (data || []) as SyncLog[];
    },
    enabled: !!activeTenantId,
  });

  // Get integration status by provider
  const getIntegrationStatus = (provider: string): IntegrationStatus => {
    const integration = integrations?.find(i => i.provider === provider);
    if (!integration) return 'not_configured';
    return integration.status;
  };

  // Get integration data by provider
  const getIntegrationData = (provider: string): Integration | undefined => {
    return integrations?.find(i => i.provider === provider);
  };

  // Handle OAuth connection for integrations
  const handleConnect = (provider: string) => {
    // For now, just expand to show the config component which handles OAuth
    setSelectedIntegration(provider);
  };

  // Count connected integrations
  const connectedCount = integrations?.filter(i => i.status === 'active').length || 0;

  if (isLoadingIntegrations) {
    return (
      <MainLayout>
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Integrations</h1>
            <p className="text-sm text-muted-foreground">Connect third-party services to enhance your workflow</p>
          </div>
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Integrations</h1>
            <p className="text-sm text-muted-foreground">
              Connect third-party services to enhance your workflow
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Plug2 className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {connectedCount} of {AVAILABLE_INTEGRATIONS.filter(i => !i.comingSoon).length} connected
            </span>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <ScrollArea className="w-full">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0">
              <TabsTrigger value="all" className="min-h-[44px] px-4">All Integrations</TabsTrigger>
              <TabsTrigger value="connected" className="min-h-[44px] px-4">
                Connected
                {connectedCount > 0 && (
                  <Badge variant="secondary" className="ml-2">{connectedCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="logs" className="min-h-[44px] px-4">
                <History className="h-4 w-4 mr-2" />
                Activity
              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" className="invisible" />
          </ScrollArea>

          {/* All Integrations Tab */}
          <TabsContent value="all" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
            {/* Selected Integration Config */}
            {selectedIntegration === 'slack' && (
              <div className="mb-6">
                <SlackConfig />
              </div>
            )}
            {selectedIntegration === 'hubspot' && (
              <div className="mb-6">
                <HubSpotConfig />
              </div>
            )}
            {selectedIntegration === 'google' && (
              <div className="mb-6">
                <GoogleConfig />
              </div>
            )}

            {/* Integration Cards Grid */}
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              {AVAILABLE_INTEGRATIONS.map(integration => {
                const integrationData = getIntegrationData(integration.id);
                const status = integration.comingSoon ? 'not_configured' : getIntegrationStatus(integration.id);

                return (
                  <div key={integration.id} className="relative">
                    {integration.comingSoon && (
                      <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                        <Badge variant="secondary" className="text-sm">Coming Soon</Badge>
                      </div>
                    )}
                    <IntegrationCard
                      name={integration.name}
                      description={integration.description}
                      icon={integration.icon}
                      iconBgClass={integration.iconBgClass}
                      status={status}
                      lastSyncAt={integrationData?.last_sync_at}
                      lastError={integrationData?.last_error}
                      onConnect={() => handleConnect(integration.id)}
                      onConfigure={() => setSelectedIntegration(
                        selectedIntegration === integration.id ? null : integration.id
                      )}
                    />
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Connected Tab */}
          <TabsContent value="connected" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
            {connectedCount === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Plug2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No integrations connected</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect your first integration to start syncing data automatically.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {integrations?.filter(i => i.status === 'active').map(integration => {
                  const config = AVAILABLE_INTEGRATIONS.find(ai => ai.id === integration.provider);
                  if (!config) return null;

                  return (
                    <div key={integration.id}>
                      {integration.provider === 'slack' && <SlackConfig />}
                      {integration.provider === 'hubspot' && <HubSpotConfig />}
                      {integration.provider === 'google' && <GoogleConfig />}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Activity/Logs Tab */}
          <TabsContent value="logs" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Sync Activity</CardTitle>
                <CardDescription>Recent integration sync events and status</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingLogs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : syncLogs && syncLogs.length > 0 ? (
                  <div className="space-y-3">
                    {syncLogs.map(log => (
                      <div
                        key={log.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Badge
                            variant={log.status === 'success' ? 'default' : 'destructive'}
                            className="capitalize"
                          >
                            {log.status}
                          </Badge>
                          <div>
                            <p className="text-sm font-medium capitalize">{log.provider}</p>
                            <p className="text-xs text-muted-foreground">{log.action}</p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground">
                      No sync activity yet. Connect an integration to start logging events.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
